import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";

import { createReference } from "@medplum/core";
import { Practitioner } from "@medplum/fhirtypes";
import { useMedplum, useMedplumProfile } from "@medplum/react";

import { Button, Group, Paper, Textarea } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { showNotification } from "@mantine/notifications";

import {
  ClientNote,
  ClientNoteForm,
  ClientNoteStatus,
  ClientNoteTypeForm,
  saveClientClinicalImpression,
  saveClientNoteForm,
} from "@/clients/zod-schemas/client-note";
import { TextWrapper } from "@/components/TextWrapper";
export default function AddClientConfidentialNotes({
  handleToggleReminder,
  setRefresh,
  background,
}: {
  handleToggleReminder: () => void;
  setRefresh: (refresh: boolean | ((prev: boolean) => boolean)) => void;
  background: string | undefined;
}) {
  const { clientId } = useParams() as { clientId: string };
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner;
  const [formTopLevelError, setFormTopLevelError] = useState<string | null>(null);

  const form = useForm<ClientNoteTypeForm>({
    initialValues: { noteStatus: ClientNoteStatus.InProgress, noteText: "" },
    validate: zodResolver(ClientNoteForm),
  });

  const handleAddNoteSubmit = useCallback(
    async (formValues: ClientNoteTypeForm) => {
      try {
        const questionnaireResponse = await saveClientNoteForm({
          medplum,
          form: formValues,
          clientId,
          practitionerReference: createReference(profile),
          type: ClientNote.Confidential,
        });
        await saveClientClinicalImpression({ medplum, questionnaireResponse });
        setRefresh((prev) => !prev);
        handleToggleReminder();
      } catch (err) {
        showNotification({
          color: "red",
          message: "Cannot save empty note",
          autoClose: false,
          styles: { description: { whiteSpace: "pre-line" } },
        });
      }
    },
    [handleToggleReminder, medplum, clientId, profile, setRefresh],
  );

  return (
    <Paper px="sm" bg={background} mb={{ base: 0, sm: "xs" }}>
      <form
        onSubmit={form.onSubmit(
          (confidentialNote) => {
            handleAddNoteSubmit(confidentialNote);
          },
          (validationErrors) => {
            if (Object.keys(validationErrors).length === 1 && validationErrors?.[""]) {
              setFormTopLevelError(validationErrors[""] as string);
            }
          },
        )}
      >
        {formTopLevelError ? (
          <Group>
            <TextWrapper size="sm" c="var(--mantine-color-error)">
              {formTopLevelError}
            </TextWrapper>
          </Group>
        ) : null}
        <Textarea
          autosize
          variant="unstyled"
          placeholder="Type note here"
          {...form.getInputProps("noteText")}
          mb="xs"
          data-testid="add-confidential-note-text"
        />
        <Group pt="xs" gap="xs" justify="flex-end">
          <Button
            color="black"
            variant="subtle"
            size="compact-xs"
            onClick={handleToggleReminder}
            data-testid="cancel-confidential-note-button"
          >
            Cancel
          </Button>
          <Button
            color="black"
            type="submit"
            variant="outline"
            size="compact-xs"
            data-testid="save-confidential-note-button"
          >
            Save
          </Button>
        </Group>
      </form>
    </Paper>
  );
}
